import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TextInput, Button, Group, Box, Text, Radio } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { ArrowNarrowLeft } from "tabler-icons-react";

import {
  CreateUser,
  useUpdateUserMutation,
  useUserQuery,
  userSchema,
} from "services/api";

const Edit = () => {
  const [updateUser] = useUpdateUserMutation();

  const form = useForm<CreateUser>({
    validate: zodResolver(userSchema),
    initialValues: {
      empNo: "",
      empName: "",
      deptId: "",
      deptName: "",
      tel: "",
      telExt: "",
      fax: "",
      email: "",
      address: "",
      description: "",
      stopYn: false,
    },
  });

  const { id } = useParams();
  const { data: user } = useUserQuery(id, { skip: id === undefined });

  useEffect(() => {
    if (user === undefined) {
      return;
    }

    form.setValues({
      empNo: user.empNo,
      empName: user.empName,
      deptId: user.deptId ?? "",
      deptName: user.deptName ?? "",
      tel: user.tel ?? "",
      telExt: user.telExt ?? "",
      fax: user.fax ?? "",
      email: user.email ?? "",
      address: user.address ?? "",
      description: user.description ?? "",
      stopYn: user.stopYn ?? false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fields: { label: string; apiAttribute: keyof CreateUser }[] = [
    {
      label: "部門代號",
      apiAttribute: "deptId",
    },
    {
      label: "部門名稱",
      apiAttribute: "deptName",
    },
    {
      label: "聯絡電話",
      apiAttribute: "tel",
    },
    {
      label: "分機",
      apiAttribute: "telExt",
    },
    {
      label: "傳真",
      apiAttribute: "fax",
    },
    {
      label: "地址",
      apiAttribute: "address",
    },
    {
      label: "描述/備註",
      apiAttribute: "description",
    },
  ];

  const navigate = useNavigate();

  const handleSubmit = async (values: CreateUser) => {
    try {
      await updateUser(values).unwrap();

      navigate(-1);
    } catch (e) {
      if (e.status === 409) {
        form.setFieldError("empNo", "員工編號重複");
        return;
      }
    }
  };

  return (
    <Box py={8} px={36}>
      <Text
        variant="link"
        component={Link}
        color="cyan"
        to="../users"
        underline={false}
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          "&:hover": {
            borderBottom: "1px solid" + theme.colors.cyan[6],
          },
        })}
      >
        <ArrowNarrowLeft size={20} /> 返回上一頁
      </Text>
      <h2>編輯人員帳號</h2>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box>
          <TextInput
            label="員工編號"
            {...form.getInputProps("empNo")}
            mb="sm"
            disabled
          />

          <TextInput
            required
            label="電子信箱"
            mb="sm"
            {...form.getInputProps("email")}
          />

          <TextInput
            required
            label="員工名稱"
            mb="sm"
            {...form.getInputProps("empName")}
          />

          {fields.map((field) => (
            <TextInput
              key={field.label}
              mb="sm"
              label={field.label}
              {...form.getInputProps(field.apiAttribute)}
            />
          ))}

          <Radio.Group
            label="帳號狀態"
            value={`${form.values.stopYn}`}
            mb="sm"
            onChange={(value) =>
              form.setValues((values) => ({
                ...values,
                stopYn: value === "true",
              }))
            }
          >
            <Radio value="false" label="啟用" />
            <Radio value="true" label="停用" />
          </Radio.Group>
        </Box>

        <Box></Box>

        <Group mt="xl">
          <Button type="submit" color="cyan">
            更新資料
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default Edit;
